// Temperature history "Letzte 20 Minuten" (FR-13): the machine's 20-minute document (history message) plus the live
// values that arrive afterwards. Samples are placed on the API's clock; without a valid machine clock the document's
// end is the moment it arrived.
import { decodeChannel, type CurveChannel } from '@brewcore/protocol';
import type { HistoryMessage, LiveDeltaMessage } from '@brewcore/app-contract';

export const HISTORY_WINDOW_MS = 20 * 60_000;

export interface HistorySample {
  /** API time (ms) */
  t: number;
  temp: number | null;
  target: number | null;
  power: number | null;
}

export interface HistoryView {
  samples: readonly HistorySample[];
  /** the machine sent an empty history (restarted recently) */
  emptyAfterRestart: boolean;
  /** boot of the document, null before one arrived */
  boot: number | null;
}

export class HistoryBuffer {
  private view: HistoryView = { samples: [], emptyAfterRestart: false, boot: null };
  private readonly listeners = new Set<() => void>();
  private lastLiveAt = 0;

  constructor(private readonly now: () => number = Date.now) {}

  subscribe = (fn: () => void): (() => void) => {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  };

  getSnapshot = (): HistoryView => this.view;

  private commit(samples: HistorySample[], patch: Partial<HistoryView> = {}): void {
    const from = this.now() - HISTORY_WINDOW_MS;
    this.view = { ...this.view, ...patch, samples: samples.filter((s) => s.t >= from) };
    for (const fn of this.listeners) fn();
  }

  /** A history document replaces everything before its end; live samples after it stay. */
  applyHistory(m: HistoryMessage): void {
    const channel = (key: string) =>
      (m.channels as unknown as CurveChannel[]).find((c) => c.key === key && Array.isArray(c.data));
    const temp = channel('temp');
    const target = channel('target');
    const power = channel('power');
    const ref = temp ?? target ?? power;
    const n = ref?.data.length ?? 0;
    const dt = ref?.dt ?? 2000;
    // with a valid machine clock the last sample is at `end`; the API and machine clocks are both NTP-close
    const endMs = m.end !== null ? m.end * 1000 : m.receivedAt;
    const values = (c: CurveChannel | undefined) => (c ? decodeChannel(c) : []);
    const tv = values(temp);
    const gv = values(target);
    const pv = values(power);
    const doc: HistorySample[] = [];
    for (let i = 0; i < n; i++)
      doc.push({
        t: endMs - (n - 1 - i) * dt,
        temp: tv[i] ?? null,
        target: gv[i] ?? null,
        power: pv[i] ?? null,
      });
    const after = this.view.samples.filter((s) => s.t > endMs);
    this.commit([...doc, ...after], { emptyAfterRestart: n === 0, boot: m.boot });
  }

  /** A live value (about 1 Hz) becomes a sample, at most one per second. */
  applyLive(m: LiveDeltaMessage): void {
    const d = m.data as { currentTemp?: unknown; targetTemp?: unknown; heaterPower?: unknown };
    if (m.receivedAt - this.lastLiveAt < 900) return;
    this.lastLiveAt = m.receivedAt;
    const num = (v: unknown) => (typeof v === 'number' ? v : null);
    this.commit([
      ...this.view.samples,
      { t: m.receivedAt, temp: num(d.currentTemp), target: num(d.targetTemp), power: num(d.heaterPower) },
    ]);
  }
}
